import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Button,
  DatePicker,
  Descriptions,
  Form,
  Input,
  Modal,
  Popconfirm,
  Radio,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd'
import dayjs from 'dayjs'
import {
  activateTenantModule,
  createTenantBillingPayment,
  deactivateTenantModule,
  getTenantModuleCatalog,
  previewTenantModuleActivation,
} from '../api'
import { fmtRub } from '../modules/list'
import { mapPaymentStatus } from '../../../utilits/payment-status'

const subscriptionStatusMap: Record<string, { label: string; color: string }> = {
  active: { label: 'Активна', color: 'green' },
  inactive: { label: 'Неактивна', color: 'default' },
  suspended: { label: 'Приостановлена', color: 'orange' },
  expired: { label: 'Истекла', color: 'red' },
  canceled: { label: 'Отменена', color: 'default' },
  cancelled: { label: 'Отменена', color: 'default' },
  pending: { label: 'Ожидание', color: 'blue' },
}

const moduleStatusMap: Record<string, { label: string; color: string }> = {
  active: { label: 'Активен', color: 'green' },
  pending_next_period: { label: 'С нового периода', color: 'orange' },
  inactive: { label: 'Не подключён', color: 'default' },
}

function fmtDateOnly(val: unknown) {
  if (!val) return '—'
  return dayjs(String(val).slice(0, 10)).format('DD.MM.YYYY')
}

function modulesLabel(row: any): string {
  const snap = row.modules_snapshot
  if (Array.isArray(snap) && snap.length) {
    return snap.map((m: any) => m.name || m.code).join(', ')
  }
  if (typeof snap === 'string') {
    try {
      const parsed = JSON.parse(snap)
      if (Array.isArray(parsed)) return parsed.map((m: any) => m.name || m.code).join(', ')
    } catch {
      /* ignore */
    }
  }
  return row.module_name || '—'
}

type Props = {
  tenantId: string
  subscription: any
  billing: any
  onReload: () => Promise<void>
}

export default function TenantSubscriptionCard({
  tenantId,
  subscription,
  billing,
  onReload,
}: Props) {
  const [catalog, setCatalog] = useState<any[]>([])
  const [addModuleOpen, setAddModuleOpen] = useState(false)
  const [addPaymentOpen, setAddPaymentOpen] = useState(false)
  const [selectedModule, setSelectedModule] = useState<string | null>(null)
  const [activationMode, setActivationMode] = useState<'now' | 'next_period'>('now')
  const [preview, setPreview] = useState<any>(null)
  const [submitting, setSubmitting] = useState(false)
  const [paymentForm] = Form.useForm()

  const modules = billing?.modules || []
  const payments = billing?.payments || []
  const term = subscription?.billing_term_months || billing?.term_months
  const subscriptionStatus = subscriptionStatusMap[String(subscription?.status || '')]

  const loadCatalog = useCallback(async () => {
    try {
      const result = await getTenantModuleCatalog(tenantId)
      setCatalog(result?.data || [])
    } catch (_e) {
      setCatalog([])
    }
  }, [tenantId])

  useEffect(() => {
    loadCatalog()
  }, [loadCatalog])

  const connectedCodes = useMemo(
    () =>
      new Set(modules.filter((m: any) => m.status !== 'inactive').map((m: any) => m.module_code)),
    [modules],
  )

  const availableToAdd = useMemo(
    () => catalog.filter(m => !connectedCodes.has(m.code)),
    [catalog, connectedCodes],
  )

  const hasBillingPeriod = Boolean(
    subscription?.current_period_start && subscription?.current_period_end,
  )

  const loadPreview = useCallback(
    async (code: string, mode: 'now' | 'next_period') => {
      try {
        const result = await previewTenantModuleActivation(tenantId, {
          module_code: code,
          activation_mode: mode,
        })
        setPreview(result?.data || null)
      } catch (_e) {
        setPreview(null)
      }
    },
    [tenantId],
  )

  useEffect(() => {
    if (addModuleOpen && selectedModule) {
      loadPreview(selectedModule, activationMode)
    }
  }, [addModuleOpen, selectedModule, activationMode, loadPreview])

  const onActivateModule = async () => {
    if (!selectedModule) return
    setSubmitting(true)
    try {
      await activateTenantModule(tenantId, selectedModule, activationMode)
      message.success('Модуль подключён')
      setAddModuleOpen(false)
      setSelectedModule(null)
      await onReload()
    } catch (e: any) {
      message.error(e?.message || 'Не удалось подключить модуль')
    } finally {
      setSubmitting(false)
    }
  }

  const onDeactivate = async (code: string) => {
    try {
      await deactivateTenantModule(tenantId, code)
      message.success('Модуль отключён')
      await onReload()
    } catch (e: any) {
      message.error(e?.message || 'Не удалось отключить модуль')
    }
  }

  const onAddPayment = async () => {
    try {
      const values = await paymentForm.validateFields()
      setSubmitting(true)
      await createTenantBillingPayment(tenantId, {
        period_start: values.period_start ? values.period_start.format('YYYY-MM-DD') : undefined,
        status: values.status || 'succeeded',
        comment: values.comment || null,
      })
      message.success('Оплата добавлена')
      setAddPaymentOpen(false)
      paymentForm.resetFields()
      await onReload()
    } catch (e: any) {
      if (e?.errorFields) return
      message.error(e?.message || 'Не удалось добавить оплату')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      <Descriptions bordered column={2} size="small" style={{ marginBottom: 16 }}>
        <Descriptions.Item label="Статус подписки">
          {subscription?.status ? (
            <Tag color={subscriptionStatus?.color ?? 'default'}>
              {subscriptionStatus?.label ?? String(subscription.status)}
            </Tag>
          ) : '—'}
        </Descriptions.Item>
        <Descriptions.Item label="Тариф">
          {term ? `${term} мес.` : '—'}
          {subscription?.pending_billing_term_months
            ? ` → ${subscription.pending_billing_term_months} мес.`
            : ''}
        </Descriptions.Item>
        <Descriptions.Item label="Сумма периода">
          {fmtRub(billing?.period_total ?? billing?.monthly_rent)}
        </Descriptions.Item>
        <Descriptions.Item label="Период">
          {hasBillingPeriod
            ? `${fmtDateOnly(subscription.current_period_start)} — ${fmtDateOnly(
                subscription.current_period_end,
              )}`
            : '—'}
        </Descriptions.Item>
        <Descriptions.Item label="Лимит пользователей">
          {subscription?.users_limit ?? '—'}
        </Descriptions.Item>
      </Descriptions>

      <Space style={{ marginBottom: 8, width: '100%', justifyContent: 'space-between' }}>
        <Typography.Text strong>Модули</Typography.Text>
        <Button
          size="small"
          type="primary"
          disabled={availableToAdd.length === 0}
          onClick={() => {
            setSelectedModule(availableToAdd[0]?.code || null)
            setActivationMode('now')
            setAddModuleOpen(true)
          }}
        >
          Подключить модуль
        </Button>
      </Space>

      <Table
        rowKey="id"
        size="small"
        dataSource={modules}
        pagination={false}
        locale={{ emptyText: 'Модули не подключены' }}
        columns={[
          { title: 'Модуль', dataIndex: 'module_name', key: 'module_name' },
          {
            title: 'Цена за тариф',
            key: 'price',
            render: (_, row: any) => fmtRub(row.price_snapshot),
          },
          {
            title: 'Статус',
            key: 'status',
            render: (_, row: any) => {
              const s = moduleStatusMap[row.status] || { label: row.status, color: 'default' }
              return <Tag color={s.color}>{s.label}</Tag>
            },
          },
          {
            title: 'С',
            key: 'activated_at',
            render: (_, row: any) =>
              row.status === 'pending_next_period'
                ? fmtDateOnly(row.scheduled_at)
                : fmtDateOnly(row.activated_at),
          },
          {
            title: '',
            key: 'actions',
            width: 120,
            render: (_, row: any) =>
              row.is_required ? (
                <Typography.Text type="secondary">Обязательный</Typography.Text>
              ) : row.status === 'active' || row.status === 'pending_next_period' ? (
                <Popconfirm
                  title="Отключить модуль?"
                  onConfirm={() => onDeactivate(row.module_code)}
                >
                  <Button size="small" danger type="link">
                    Отключить
                  </Button>
                </Popconfirm>
              ) : null,
          },
        ]}
        style={{ marginBottom: 24 }}
      />

      <Space style={{ marginBottom: 8, width: '100%', justifyContent: 'space-between' }}>
        <Typography.Text strong>История оплат</Typography.Text>
        <Button size="small" onClick={() => setAddPaymentOpen(true)}>
          Добавить оплату периода
        </Button>
      </Space>

      <Table
        rowKey="id"
        size="small"
        dataSource={payments}
        pagination={false}
        locale={{ emptyText: 'Нет оплат' }}
        columns={[
          {
            title: 'Период',
            key: 'period',
            render: (_, row: any) =>
              `${fmtDateOnly(row.period_start)} — ${fmtDateOnly(row.period_end)}`,
          },
          {
            title: 'Тариф',
            key: 'term',
            render: (_, row: any) =>
              row.billing_term_months ? `${row.billing_term_months} мес.` : '—',
          },
          {
            title: 'Модули',
            key: 'modules',
            render: (_, row: any) => modulesLabel(row),
          },
          {
            title: 'Сумма',
            key: 'amount',
            render: (_, row: any) => fmtRub(row.amount),
          },
          {
            title: 'Статус',
            key: 'status',
            render: (_, row: any) => {
              const s = mapPaymentStatus(String(row.status || ''))
              return <Tag color={s.color}>{s.label}</Tag>
            },
          },
        ]}
      />

      <Modal
        open={addModuleOpen}
        title="Подключить модуль"
        onCancel={() => setAddModuleOpen(false)}
        onOk={onActivateModule}
        confirmLoading={submitting}
        okText="Подключить"
      >
        <Space direction="vertical" style={{ width: '100%' }} size={12}>
          <Select
            style={{ width: '100%' }}
            placeholder="Выберите модуль"
            value={selectedModule}
            options={availableToAdd.map(m => ({
              value: m.code,
              label: `${m.name} (${fmtRub(m.price_total || m.price_monthly)})`,
            }))}
            onChange={setSelectedModule}
          />
          {hasBillingPeriod && (
            <Radio.Group value={activationMode} onChange={e => setActivationMode(e.target.value)}>
              <Space direction="vertical">
                <Radio value="now">
                  Сейчас
                  {preview?.price_total != null && (
                    <Typography.Text type="secondary">
                      {' '}
                      — {fmtRub(preview.price_total)} / {preview.term_months} мес.
                    </Typography.Text>
                  )}
                </Radio>
                <Radio value="next_period">
                  С нового периода
                  {preview?.scheduled_at && activationMode === 'next_period' && (
                    <Typography.Text type="secondary">
                      {' '}
                      — с {fmtDateOnly(preview.scheduled_at)}
                    </Typography.Text>
                  )}
                </Radio>
              </Space>
            </Radio.Group>
          )}
        </Space>
      </Modal>

      <Modal
        open={addPaymentOpen}
        title="Оплата периода"
        onCancel={() => setAddPaymentOpen(false)}
        onOk={onAddPayment}
        confirmLoading={submitting}
        okText="Создать"
      >
        <Typography.Paragraph type="secondary">
          Сумма рассчитывается автоматически по тарифу ({term || '?'} мес.) и подключённым модулям.
        </Typography.Paragraph>
        <Form form={paymentForm} layout="vertical" initialValues={{ status: 'succeeded' }}>
          <Form.Item name="period_start" label="Начало периода (необязательно)">
            <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" />
          </Form.Item>
          <Form.Item name="status" label="Статус">
            <Select
              options={[
                { value: 'succeeded', label: 'Успешно (продлить период)' },
                { value: 'pending', label: 'Ожидает' },
              ]}
            />
          </Form.Item>
          <Form.Item name="comment" label="Комментарий">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}
